import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  Alert,
  Typography,
  TextField,
  IconButton,
  MenuItem,
  Grid,
  SelectChangeEvent,
  SxProps,
  Theme,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import api from "../../../api/axiosConfig";
import { endpoints } from "../../../api/endpoints";
import stateCityMap from "../../../constants/stateCityMap.json";
import { getApiErrorMessage } from "../../../utils/apiError";
import { colors } from "../../../theme/colors";

interface StationFormData {
  stationType: string;
  stationName: string;
  ownershipType: string;
  city: string;
  state: string;
  pinCode: string;
  stationAddress: string;
  cgdName: string;
  pesoApprovalNumber: string;
  assignManager: string;
}

interface AddStationModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: StationFormData) => Promise<boolean>;
  isSubmitting?: boolean;
  errorMessage?: string | null;
  onClearError?: () => void;
  initialData?: StationFormData;
  isEditMode?: boolean;
}

interface TouchedFields {
  stationType?: boolean;
  stationName?: boolean;
  ownershipType?: boolean;
  city?: boolean;
  state?: boolean;
  pinCode?: boolean;
  stationAddress?: boolean;
  cgdName?: boolean;
  pesoApprovalNumber?: boolean;
  assignManager?: boolean;
}

interface ManagerOption {
  id: string;
  user_id: string;
  name: string;
}

interface ManagersByStationType {
  station_type?: string;
  managers?: ManagerOption[];
}

interface ManagerIdsResponse {
  items?: ManagersByStationType[];
}

const stationTypes = [
  { label: "Fuel Station", value: "FUEL_STATION" },
  { label: "Testing Center", value: "TESTING_CENTER" },
];

const ownershipOptions = [
  { label: "COCO", value: "COCO" },
  { label: "CODO", value: "CODO" },
  { label: "DODO", value: "DODO" },
];

const cgdOptions = [
  {
    label: "Adani Total Gas Limited (ATGL)",
    value: "Adani Total Gas Limited (ATGL)",
  },
  {
    label: "Indraprastha Gas Limited (IGL)",
    value: "Indraprastha Gas Limited (IGL)",
  },
];

const locationMap = stateCityMap as Record<string, string[]>;

const defaultFormData: StationFormData = {
  stationType: "FUEL_STATION",
  stationName: "",
  ownershipType: "",
  city: "",
  state: "",
  pinCode: "",
  stationAddress: "",
  cgdName: "",
  pesoApprovalNumber: "",
  assignManager: "",
};

const normalizeStationFormData = (data: StationFormData): StationFormData => ({
  stationType: data.stationType,
  stationName: data.stationName.trim(),
  ownershipType: data.ownershipType,
  city: data.city.trim(),
  state: data.state.trim(),
  pinCode: data.pinCode.trim(),
  stationAddress: data.stationAddress.trim(),
  cgdName: data.cgdName,
  pesoApprovalNumber: data.pesoApprovalNumber.trim(),
  assignManager: data.assignManager,
});

const fieldLabelMap: Record<keyof StationFormData, string> = {
  stationType: "Station Type",
  stationName: "Station Name",
  ownershipType: "Ownership Type",
  city: "City",
  state: "State",
  pinCode: "Pin Code",
  stationAddress: "Station Address",
  cgdName: "CGD Name",
  pesoApprovalNumber: "PESO Approval Number",
  assignManager: "Assign Manager",
};

const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 1.5,
    backgroundColor: "#FFFFFF",
    minHeight: 56,
    "& fieldset": {
      borderColor: "#D0D5DD",
    },
    "&:hover fieldset": {
      borderColor: "#98A2B3",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#2F7D32",
      borderWidth: 1,
    },
  },
  "& .MuiInputBase-input": {
    fontSize: "16px",
    color: "#344054",
  },
  "& .MuiFormHelperText-root": {
    marginLeft: 0,
  },
};

const getSelectDisplayValue = (
  value: string,
  options: Array<{ label: string; value: string }>,
  placeholder: string,
) => {
  if (!value) {
    return (
      <Box component="span" sx={{ color: "#98A2B3" }}>
        {placeholder}
      </Box>
    );
  }

  return options.find((option) => option.value === value)?.label || value;
};

const FieldLabel: React.FC<{
  label: string;
  required?: boolean;
  optional?: boolean;
  sx?: SxProps<Theme>;
}> = ({ label, required = false, optional = false, sx }) => (
  <Typography
    sx={{
      mb: 1,
      fontSize: "14px",
      fontWeight: 600,
      color: "#1F2937",
      ...sx,
    }}
  >
    {label}
    {required ? " *" : ""}
    {optional ? " (Optional)" : ""}
  </Typography>
);

const AddStationModal: React.FC<AddStationModalProps> = ({
  open,
  onClose,
  onSave,
  isSubmitting = false,
  errorMessage = null,
  onClearError,
  initialData,
  isEditMode = false,
}) => {
  const [formData, setFormData] = useState<StationFormData>(defaultFormData);
  const [errors, setErrors] = useState<Partial<StationFormData>>({});
  const [touched, setTouched] = useState<TouchedFields>({});
  const [managerGroups, setManagerGroups] = useState<ManagersByStationType[]>(
    [],
  );
  const [isManagersLoading, setIsManagersLoading] = useState(false);
  const [managerError, setManagerError] = useState<string | null>(null);

  const isTestingStation = formData.stationType === "TESTING_CENTER";
  const stateOptions = useMemo(
    () =>
      Object.keys(locationMap).map((state) => ({ label: state, value: state })),
    [],
  );
  const cityOptions = useMemo(
    () =>
      (locationMap[formData.state] || []).map((city) => ({
        label: city,
        value: city,
      })),
    [formData.state],
  );
  const filteredManagers = useMemo(() => {
    if (!formData.stationType) {
      return [];
    }

    const matchingGroup = managerGroups.find(
      (group) => group.station_type === formData.stationType,
    );

    return matchingGroup?.managers || [];
  }, [formData.stationType, managerGroups]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const controller = new AbortController();

    const fetchManagers = async () => {
      setIsManagersLoading(true);
      setManagerError(null);

      try {
        const response = await api.get<ManagerIdsResponse>(
          endpoints.users.getManagerIds,
          { signal: controller.signal },
        );

        setManagerGroups(
          Array.isArray(response.data?.items) ? response.data.items : [],
        );
      } catch (error: any) {
        if (error?.name === "CanceledError" || error?.code === "ERR_CANCELED") {
          return;
        }

        const message = getApiErrorMessage(
          error,
          "Failed to load managers. Please try again.",
        );

        setManagerError(message);
        setManagerGroups([]);
      } finally {
        setIsManagersLoading(false);
      }
    };

    fetchManagers();

    return () => {
      controller.abort();
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setFormData({
      ...defaultFormData,
      ...(initialData || {}),
    });
    setErrors({});
    setTouched({});
  }, [open, initialData]);

  const handleInputChange =
    (field: keyof StationFormData) =>
    (
      event:
        | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
        | SelectChangeEvent,
    ) => {
      const value = event.target.value;
      onClearError?.();

      if (field === "stationType") {
        setFormData((prev) => ({
          ...prev,
          stationType: value,
          assignManager: "",
        }));
        setTouched((prev) => ({
          ...prev,
          stationType: true,
          assignManager: false,
          ownershipType: false,
          cgdName: false,
          pesoApprovalNumber: false,
        }));
        setErrors((prev) => ({
          ...prev,
          stationType: undefined,
          assignManager: undefined,
          ownershipType: undefined,
          cgdName: undefined,
          pesoApprovalNumber: undefined,
        }));
      } else if (field === "state") {
        setFormData((prev) => ({
          ...prev,
          state: value,
          city: "",
        }));
        setTouched((prev) => ({
          ...prev,
          state: true,
          city: false,
        }));
        setErrors((prev) => ({
          ...prev,
          state: undefined,
          city: undefined,
        }));
      } else if (field === "pinCode") {
        const numericPinCode = value.replace(/\D/g, "").slice(0, 6);

        setFormData((prev) => ({ ...prev, pinCode: numericPinCode }));
        setTouched((prev) => ({ ...prev, pinCode: true }));
        if (errors.pinCode) {
          setErrors((prev) => ({ ...prev, pinCode: undefined }));
        }
      } else {
        setFormData((prev) => ({ ...prev, [field]: value }));
        setTouched((prev) => ({ ...prev, [field]: true }));
        if (errors[field]) {
          setErrors((prev) => ({ ...prev, [field]: undefined }));
        }
      }
    };

  const requiredFields = useMemo<Array<keyof StationFormData>>(() => {
    const baseFields: Array<keyof StationFormData> = [
      "stationType",
      "stationName",
      "city",
      "state",
      "pinCode",
      "stationAddress",
      "assignManager",
    ];

    if (!isTestingStation) {
      baseFields.push("ownershipType", "cgdName");
    }

    return baseFields;
  }, [isTestingStation]);

  const validateField = (
    field: keyof StationFormData,
    value: string,
  ): string => {
    if (requiredFields.includes(field) && (!value || value.trim() === "")) {
      return `${fieldLabelMap[field]} is required`;
    }

    if (field === "pinCode" && !/^\d{6}$/.test(value)) {
      return "Pin Code must be 6 digits";
    }

    return "";
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<StationFormData> = {};
    const nextTouched: TouchedFields = {};
    let isValid = true;

    (Object.keys(formData) as Array<keyof StationFormData>).forEach((field) => {
      const shouldValidate =
        requiredFields.includes(field) ||
        field === "pinCode" ||
        (isTestingStation && field === "pesoApprovalNumber");

      if (!shouldValidate) {
        return;
      }

      nextTouched[field] = true;
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
        isValid = false;
      }
    });

    setTouched((prev) => ({ ...prev, ...nextTouched }));
    setErrors(newErrors);
    return isValid;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    const isSaved = await onSave(formData);
    if (!isSaved) {
      return;
    }

    handleReset();
    onClose();
  };

  const handleReset = () => {
    setFormData(defaultFormData);
    setErrors({});
    setTouched({});
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const isFieldError = (field: keyof StationFormData): boolean => {
    return !!errors[field] && !!touched[field];
  };

  const getFieldError = (field: keyof StationFormData): string => {
    return errors[field] || "";
  };

  const hasEditFormChanges = (() => {
    if (!isEditMode || !initialData) {
      return true;
    }

    const initialFormData = normalizeStationFormData({
      ...defaultFormData,
      ...initialData,
    });
    const currentFormData = normalizeStationFormData(formData);

    return (Object.keys(defaultFormData) as Array<keyof StationFormData>).some(
      (field) => currentFormData[field] !== initialFormData[field],
    );
  })();

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 4,
            maxHeight: "90vh",
            maxWidth: 760,
            boxShadow: "0 28px 60px rgba(15, 23, 42, 0.22)",
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          px: { xs: 3, sm: 5 },
          pt: { xs: 3, sm: 4 },
          pb: 1.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <Box>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "24px",
              color: colors.black,
              lineHeight: 1.1,
              fontFamily: "'Inter', sans-serif",
            }}
          >
            {isEditMode ? "Edit Station" : "Add New Station"}
          </Typography>
          <Typography
            sx={{
              color: colors.textGray,
              mt: 1,
              fontSize: "14px",
              fontFamily: "'Inter', sans-serif",
              fontWeight: 400,
            }}
          >
            {isEditMode
              ? "Update the details for this station"
              : "Fill in the details to register a station"}
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            width: 56,
            height: 56,
            bgcolor: "#F3F4F6",
            color: "#111827",
            "&:hover": {
              bgcolor: "#E5E7EB",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: { xs: 3, sm: 5 }, pb: { xs: 4, sm: 5 }, pt: 1 }}>
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}

        {managerError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {managerError}
          </Alert>
        )}

        <Grid container spacing={2.5}>
          <Grid size={{ xs: 12 }}>
            <FieldLabel
              label="Station Type"
              required
              sx={{
                color: colors.black,
                fontFamily: "'Inter', sans-serif",
                fontSize: "14px",
                fontWeight: 600,
              }}
            />
            <TextField
              select
              fullWidth
              value={formData.stationType}
              onChange={handleInputChange("stationType")}
              onBlur={() =>
                setTouched((prev) => ({ ...prev, stationType: true }))
              }
              error={isFieldError("stationType")}
              slotProps={{
                select: {
                  displayEmpty: true,
                  renderValue: (selected: unknown) =>
                    getSelectDisplayValue(
                      selected as string,
                      stationTypes,
                      "Select station type",
                    ),
                },
              }}
              sx={textFieldSx}
            >
              {stationTypes.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </TextField>
            {isFieldError("stationType") && (
              <Typography
                variant="caption"
                sx={{ color: "#EF4444", mt: 0.75, display: "block" }}
              >
                {getFieldError("stationType")}
              </Typography>
            )}
          </Grid>

          <Grid size={{ xs: 12 }}>
            <FieldLabel
              label="Station Name"
              required
              sx={{
                color: colors.black,
                fontFamily: "'Inter', sans-serif",
                fontSize: "14px",
                fontWeight: 600,
              }}
            />
            <TextField
              fullWidth
              placeholder="e.g. Andheri East LPG"
              value={formData.stationName}
              onChange={handleInputChange("stationName")}
              onBlur={() =>
                setTouched((prev) => ({ ...prev, stationName: true }))
              }
              error={isFieldError("stationName")}
              helperText={
                isFieldError("stationName") ? getFieldError("stationName") : ""
              }
              sx={textFieldSx}
            />
          </Grid>

          {!isTestingStation && (
            <Grid size={{ xs: 12 }}>
              <FieldLabel
                label="Ownership Type"
                required
                sx={{
                  color: colors.black,
                  fontFamily: "'Inter', sans-serif",
                  fontSize: "14px",
                  fontWeight: 600,
                }}
              />
              <TextField
                select
                fullWidth
                value={formData.ownershipType}
                onChange={handleInputChange("ownershipType")}
                onBlur={() =>
                  setTouched((prev) => ({ ...prev, ownershipType: true }))
                }
                error={isFieldError("ownershipType")}
                slotProps={{
                  select: {
                    displayEmpty: true,
                    renderValue: (selected: unknown) =>
                      getSelectDisplayValue(
                        selected as string,
                        ownershipOptions,
                        "Select ownership type",
                      ),
                  },
                }}
                sx={textFieldSx}
              >
                {ownershipOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              {isFieldError("ownershipType") && (
                <Typography
                  variant="caption"
                  sx={{ color: "#EF4444", mt: 0.75, display: "block" }}
                >
                  {getFieldError("ownershipType")}
                </Typography>
              )}
            </Grid>
          )}

          {isTestingStation && (
            <Grid size={{ xs: 12 }}>
              <FieldLabel
                label="PESO Approval Number"
                optional
                sx={{
                  color: colors.black,
                  fontFamily: "'Inter', sans-serif",
                  fontSize: "14px",
                  fontWeight: 600,
                }}
              />
              <TextField
                fullWidth
                placeholder="0009999"
                value={formData.pesoApprovalNumber}
                onChange={handleInputChange("pesoApprovalNumber")}
                onBlur={() =>
                  setTouched((prev) => ({ ...prev, pesoApprovalNumber: true }))
                }
                error={isFieldError("pesoApprovalNumber")}
                helperText={
                  isFieldError("pesoApprovalNumber")
                    ? getFieldError("pesoApprovalNumber")
                    : ""
                }
                sx={textFieldSx}
              />
            </Grid>
          )}

          <Grid size={{ xs: 12, md: 4 }}>
            <FieldLabel
              label="State"
              required
              sx={{
                color: colors.black,
                fontFamily: "'Inter', sans-serif",
                fontSize: "14px",
                fontWeight: 600,
              }}
            />
            <TextField
              select
              fullWidth
              value={formData.state}
              onChange={handleInputChange("state")}
              onBlur={() => setTouched((prev) => ({ ...prev, state: true }))}
              error={isFieldError("state")}
              slotProps={{
                select: {
                  displayEmpty: true,
                  renderValue: (selected: unknown) =>
                    getSelectDisplayValue(
                      selected as string,
                      stateOptions,
                      "Select state",
                    ),
                },
              }}
              sx={textFieldSx}
            >
              {stateOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            {isFieldError("state") && (
              <Typography
                variant="caption"
                sx={{ color: "#EF4444", mt: 0.75, display: "block" }}
              >
                {getFieldError("state")}
              </Typography>
            )}
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <FieldLabel
              label="City"
              required
              sx={{
                color: colors.black,
                fontFamily: "'Inter', sans-serif",
                fontSize: "14px",
                fontWeight: 600,
              }}
            />
            <TextField
              select
              fullWidth
              disabled={!formData.state}
              value={formData.city}
              onChange={handleInputChange("city")}
              onBlur={() => setTouched((prev) => ({ ...prev, city: true }))}
              error={isFieldError("city")}
              slotProps={{
                select: {
                  displayEmpty: true,
                  renderValue: (selected: unknown) =>
                    getSelectDisplayValue(
                      selected as string,
                      cityOptions,
                      "Select city",
                    ),
                },
              }}
              sx={textFieldSx}
            >
              {cityOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            {!formData.state && (
              <Typography
                variant="caption"
                sx={{ color: "#64748B", mt: 0.75, display: "block" }}
              >
                Select a state to load cities.
              </Typography>
            )}
            {isFieldError("city") && (
              <Typography
                variant="caption"
                sx={{ color: "#EF4444", mt: 0.75, display: "block" }}
              >
                {getFieldError("city")}
              </Typography>
            )}
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <FieldLabel
              label="Pin Code"
              required
              sx={{
                color: colors.black,
                fontFamily: "'Inter', sans-serif",
                fontSize: "14px",
                fontWeight: 600,
              }}
            />
            <TextField
              fullWidth
              placeholder="400069"
              value={formData.pinCode}
              onChange={handleInputChange("pinCode")}
              onBlur={() => setTouched((prev) => ({ ...prev, pinCode: true }))}
              error={isFieldError("pinCode")}
              helperText={
                isFieldError("pinCode") ? getFieldError("pinCode") : ""
              }
              slotProps={{
                htmlInput: {
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                  maxLength: 6,
                },
              }}
              sx={textFieldSx}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <FieldLabel
              label="Station Address"
              required
              sx={{
                color: colors.black,
                fontFamily: "'Inter', sans-serif",
                fontSize: "14px",
                fontWeight: 600,
              }}
            />
            <TextField
              fullWidth
              placeholder="Enter full address of the station"
              multiline
              rows={4}
              value={formData.stationAddress}
              onChange={handleInputChange("stationAddress")}
              onBlur={() =>
                setTouched((prev) => ({ ...prev, stationAddress: true }))
              }
              error={isFieldError("stationAddress")}
              helperText={
                isFieldError("stationAddress")
                  ? getFieldError("stationAddress")
                  : ""
              }
              sx={textFieldSx}
            />
          </Grid>

          {!isTestingStation && (
            <Grid size={{ xs: 12 }}>
              <FieldLabel
                label="CGD Name"
                required
                sx={{
                  color: colors.black,
                  fontFamily: "'Inter', sans-serif",
                  fontSize: "14px",
                  fontWeight: 600,
                }}
              />
              <TextField
                select
                fullWidth
                value={formData.cgdName}
                onChange={handleInputChange("cgdName")}
                onBlur={() =>
                  setTouched((prev) => ({ ...prev, cgdName: true }))
                }
                error={isFieldError("cgdName")}
                slotProps={{
                  select: {
                    displayEmpty: true,
                    renderValue: (selected: unknown) =>
                      getSelectDisplayValue(
                        selected as string,
                        cgdOptions,
                        "Select CGD name",
                      ),
                  },
                }}
                sx={textFieldSx}
              >
                {cgdOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              {isFieldError("cgdName") && (
                <Typography
                  variant="caption"
                  sx={{ color: "#EF4444", mt: 0.75, display: "block" }}
                >
                  {getFieldError("cgdName")}
                </Typography>
              )}
            </Grid>
          )}

          <Grid size={{ xs: 12 }}>
            <FieldLabel
              label="Assign Manager"
              required
              sx={{
                color: colors.black,
                fontFamily: "'Inter', sans-serif",
                fontSize: "14px",
                fontWeight: 600,
              }}
            />
            <TextField
              select
              fullWidth
              disabled={!formData.stationType || isManagersLoading}
              value={formData.assignManager}
              onChange={handleInputChange("assignManager")}
              onBlur={() =>
                setTouched((prev) => ({ ...prev, assignManager: true }))
              }
              error={isFieldError("assignManager")}
              slotProps={{
                select: {
                  displayEmpty: true,
                  MenuProps: {
                    slotProps: {
                      paper: {
                        sx: {
                          maxHeight: 320,
                        },
                      },
                    },
                  },
                  renderValue: (selected: unknown) => {
                    if (isManagersLoading) {
                      return "Loading managers...";
                    }

                    const selectedValue = selected as string;

                    if (!selectedValue) {
                      return (
                        <Box component="span" sx={{ color: "#98A2B3" }}>
                          Select station manager
                        </Box>
                      );
                    }

                    return (
                      filteredManagers.find(
                        (manager) => manager.id === selectedValue,
                      )?.name || selectedValue
                    );
                  },
                },
              }}
              sx={textFieldSx}
            >
              {filteredManagers.map((manager) => (
                <MenuItem key={manager.id} value={manager.id}>
                  <Box>
                    <Typography variant="body2">{manager.name}</Typography>
                    <Typography variant="caption" sx={{ color: "#64748B" }}>
                      {manager.user_id}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </TextField>
            {!formData.stationType && (
              <Typography
                variant="caption"
                sx={{ color: "#64748B", mt: 0.75, display: "block" }}
              >
                Select a station type to load managers.
              </Typography>
            )}
            {formData.stationType && isManagersLoading && (
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.75 }}
              >
                <CircularProgress size={14} />
                <Typography variant="caption" sx={{ color: "#64748B" }}>
                  Loading managers...
                </Typography>
              </Box>
            )}
            {formData.stationType &&
              !isManagersLoading &&
              filteredManagers.length === 0 &&
              !managerError && (
                <Typography
                  variant="caption"
                  sx={{ color: "#64748B", mt: 0.75, display: "block" }}
                >
                  No managers available for the selected station type.
                </Typography>
              )}
            {isFieldError("assignManager") && (
              <Typography
                variant="caption"
                sx={{ color: "#EF4444", mt: 0.75, display: "block" }}
              >
                {getFieldError("assignManager")}
              </Typography>
            )}
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Button
              onClick={handleSave}
              variant="contained"
              fullWidth
              disabled={isSubmitting || (isEditMode && !hasEditFormChanges)}
              sx={{
                mt: 0.5,
                minHeight: 56,
                borderRadius: 1.5,
                bgcolor: "#2F7D32",
                color: "#FFFFFF",
                fontSize: "16px",
                fontWeight: 700,
                textTransform: "none",
                boxShadow: "0 4px 10px rgba(47, 125, 50, 0.22)",
                "&:hover": {
                  bgcolor: "#27672A",
                },
              }}
            >
              {isSubmitting
                ? isEditMode
                  ? "Updating..."
                  : "Saving..."
                : isEditMode
                  ? "Update Station"
                  : "Save Station"}
            </Button>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default AddStationModal;
